import { apiRequest } from "./client";
import { normalizeConversation } from "./conversation-lifecycle-wire";
import type { SpaceMessage } from "./spaces";

export type ConversationArchiveState = "active" | "archived";

export interface ConversationCapabilities {
  search: boolean;
  delete: boolean;
}

export interface ConversationActionState {
  allowed: boolean;
  reason: string | null;
}

export interface ConversationLifecycleState {
  lifecycle: "open" | "archived";
  archived: boolean;
  runtime: {
    state: "detached" | "activating" | "idle" | "running" | "suspended";
    generation: number;
    runtime_session_status:
      | "active"
      | "idle"
      | "suspended"
      | "ended"
      | "compacted_into"
      | null;
    availability: "reuse_segment" | "create_segment" | "unavailable";
  };
  execution: {
    id: string;
    status:
      | "queued"
      | "running"
      | "completed"
      | "failed"
      | "cancelled"
      | "deferred"
      | "waiting_approval"
      | "paused";
    error_kind: string | null;
    is_active: boolean;
  } | null;
  actions: {
    resume: ConversationActionState;
    stop: ConversationActionState;
    archive: ConversationActionState;
    unarchive: ConversationActionState;
    delete: ConversationActionState;
  };
}

export interface DurableConversation {
  id: string;
  title: string;
  /** Latest assistant prediction, null when the latest message cannot predict. */
  pending_next_prompt?: string | null;
  /** Optional only so cached rows from the pre-display-title release remain readable. */
  display_title?: string;
  /** Latest persisted runtime segment, including closed and archived chats. */
  frame_runtime_segment?: {
    runtime_session_id: string;
    model: string | null;
    adapter_type?: string | null;
    thinking_effort?: string | null;
    runtime_source?: string | null;
    /** The backend no longer resumes this runtime; a reply uses the template. */
    runtime_override_expired?: boolean;
    context_percent: number | null;
    context_used_tokens?: number | null;
    input_tokens?: number | null;
    output_tokens?: number | null;
    context_window_max?: number | null;
  } | null;
  status: "active";
  archived_at: string | null;
  message_count: number;
  last_activity_at: string | null;
  activity_at: string;
  created_at: string;
  updated_at: string;
  context_kind: "manual" | "task" | "system_wake";
  context_key: string;
  project_id: string;
  project_name: string;
  project_status: "active" | "archived";
  /** @deprecated Compatibility metadata for older runtime clients. */
  space_id?: string;
  /** @deprecated Compatibility metadata for older runtime clients. */
  space_name?: string;
  project_agent_id: string | null;
  agent_name: string | null;
  task_id: string | null;
  task?: {
    id: string;
    title: string;
    ticket_display: string | null;
    status: string;
  } | null;
  runtime: {
    state: "detached" | "activating" | "idle" | "running" | "suspended";
    generation: number;
    foreground_runtime_session_id: string | null;
    active_execution_id: string | null;
  } | null;
  /** Optional only so cached rows from the pre-projection release remain readable. */
  lifecycle?: ConversationLifecycleState;
}

interface Pagination {
  has_more: boolean;
  next_cursor: string | null;
}

export interface ConversationPage {
  data: DurableConversation[];
  pagination: Pagination;
  capabilities: ConversationCapabilities;
}

export interface ConversationHistoryPage {
  data: SpaceMessage[];
  pagination: Pagination;
  activity?: SpaceMessage[];
  activity_pagination?: Pagination;
  integrity: {
    sequence_complete: boolean;
    message_count_matches: boolean;
    ordering: "sequence_id" | "created_at_id_fallback";
  } | null;
}

interface ConversationListResponse extends Omit<
  ConversationPage,
  "capabilities"
> {
  status: "success";
  capabilities?: Partial<ConversationCapabilities>;
}

interface ConversationHistoryResponse extends ConversationHistoryPage {
  status: "success";
}

interface ConversationDetailResponse {
  status: "success";
  data: DurableConversation;
}

interface ConversationMutationResponse {
  status: "success";
  data: DurableConversation;
}

export interface ConversationSelectionReceipt {
  conversation_id: string;
  project_id: string;
  /** @deprecated Compatibility metadata from older servers. */
  space_id?: string;
  project_agent_id: string;
  agent_key: string;
  selection_generation: number;
  runtime_session_id: string | null;
  runtime_generation: number;
  runtime_state: "detached" | "activating" | "idle" | "running" | "suspended";
  active_execution_id: string | null;
}

export interface ConversationRuntimePurgeTarget {
  adapter_type: string;
  runtime_storage_key: string;
}

export interface ConversationDeletionReceipt {
  conversation_id: string;
  deletion_id: string;
  state: "purge_required" | "deleted";
  runtime_targets: ConversationRuntimePurgeTarget[];
  runtime_target_count: number;
  deleted_at: string | null;
  deleted_message_count: number;
  deleted_automatic_summary_count: number;
  deleted_execution_count: number;
  deleted_session_count: number;
  selection_generation: number;
}

interface ConversationSelectionResponse {
  status: "success";
  data: ConversationSelectionReceipt;
}

interface ConversationDeletionResponse {
  status: "success";
  data: ConversationDeletionReceipt;
}

export async function listDurableConversations(options: {
  projectId?: string | null;
  archiveState: ConversationArchiveState;
  query?: string;
  cursor?: string;
  limit?: number;
}): Promise<ConversationPage> {
  const params = new URLSearchParams({
    archive_state: options.archiveState,
    limit: String(options.limit ?? 50),
  });
  if (options.query) params.set("q", options.query);
  if (options.cursor) params.set("cursor", options.cursor);
  if (options.projectId) params.set("project_id", options.projectId);
  const response = await apiRequest<ConversationListResponse>(
    `/api/v1/conversations/?${params.toString()}`,
    { method: "GET", silent: true },
  );
  return {
    ...response,
    data: response.data.map(normalizeConversation),
    capabilities: {
      search: response.capabilities?.search === true,
      delete: response.capabilities?.delete === true,
    },
  };
}

export async function listConversationHistory(
  conversationId: string,
  options: {
    cursor?: string;
    activityCursor?: string;
    limit?: number;
    includeMessages?: boolean;
    includeActivity?: boolean;
  } = {},
): Promise<ConversationHistoryPage> {
  const params = new URLSearchParams({ limit: String(options.limit ?? 50) });
  if (options.cursor) params.set("cursor", options.cursor);
  if (options.activityCursor) {
    params.set("activity_cursor", options.activityCursor);
  }
  if (options.includeMessages !== undefined) {
    params.set("include_messages", String(options.includeMessages));
  }
  if (options.includeActivity !== undefined) {
    params.set("include_activity", String(options.includeActivity));
  }
  const response = await apiRequest<ConversationHistoryResponse>(
    `/api/v1/conversations/${conversationId}/history/?${params.toString()}`,
    { method: "GET", silent: true },
  );
  return response;
}

export async function getDurableConversation(
  conversationId: string,
): Promise<DurableConversation> {
  const response = await apiRequest<ConversationDetailResponse>(
    `/api/v1/conversations/${conversationId}/`,
    { method: "GET", silent: true },
  );
  return normalizeConversation(response.data);
}

export async function renameDurableConversation(
  conversation: Pick<DurableConversation, "id" | "project_id">,
  title: string,
): Promise<void> {
  await apiRequest(
    `/api/v1/projects/${conversation.project_id}/conversations/${conversation.id}/`,
    {
      method: "PATCH",
      body: { title },
      silent: true,
    },
  );
}

export async function selectConversation(
  conversationId: string,
): Promise<ConversationSelectionReceipt> {
  const response = await apiRequest<ConversationSelectionResponse>(
    `/api/v1/conversations/${conversationId}/select/`,
    { method: "POST", silent: true },
  );
  const data = response.data as unknown;
  if (!isConversationSelectionReceipt(data, conversationId)) {
    throw new TypeError("Invalid chat selection response");
  }
  return data;
}

function isConversationDeletionReceipt(
  value: unknown,
  conversationId: string,
  deletionId: string,
): value is ConversationDeletionReceipt {
  if (!value || typeof value !== "object") return false;
  const receipt = value as Record<string, unknown>;
  const targets = receipt.runtime_targets;
  return (
    receipt.conversation_id === conversationId &&
    typeof receipt.deletion_id === "string" &&
    (receipt.deletion_id === deletionId || receipt.deletion_id.length > 0) &&
    (receipt.state === "purge_required" || receipt.state === "deleted") &&
    Array.isArray(targets) &&
    targets.every(
      (target) =>
        target &&
        typeof target === "object" &&
        typeof (target as Record<string, unknown>).adapter_type === "string" &&
        typeof (target as Record<string, unknown>).runtime_storage_key ===
          "string",
    ) &&
    Number.isInteger(receipt.runtime_target_count) &&
    Number.isInteger(receipt.deleted_message_count) &&
    Number.isInteger(receipt.deleted_automatic_summary_count) &&
    Number.isInteger(receipt.deleted_execution_count) &&
    Number.isInteger(receipt.deleted_session_count) &&
    Number.isInteger(receipt.selection_generation) &&
    (receipt.deleted_at === null || typeof receipt.deleted_at === "string") &&
    (receipt.state !== "deleted" || targets.length === 0)
  );
}

async function mutateConversationDeletion(
  conversationId: string,
  deletionId: string,
  finalize: boolean,
): Promise<ConversationDeletionReceipt> {
  const suffix = finalize ? "delete/finalize/" : "delete/";
  const response = await apiRequest<ConversationDeletionResponse>(
    `/api/v1/conversations/${conversationId}/${suffix}`,
    {
      method: "POST",
      body: {
        deletion_id: deletionId,
        confirm_conversation_id: conversationId,
      },
      silent: true,
    },
  );
  const data = response.data as unknown;
  if (!isConversationDeletionReceipt(data, conversationId, deletionId)) {
    throw new TypeError("Invalid Delete Chat response");
  }
  return data;
}

export function prepareConversationDeletion(
  conversationId: string,
  deletionId: string,
): Promise<ConversationDeletionReceipt> {
  return mutateConversationDeletion(conversationId, deletionId, false);
}

export function finalizeConversationDeletion(
  conversationId: string,
  deletionId: string,
): Promise<ConversationDeletionReceipt> {
  return mutateConversationDeletion(conversationId, deletionId, true);
}

function isConversationSelectionReceipt(
  value: unknown,
  conversationId: string,
): value is ConversationSelectionReceipt {
  if (!value || typeof value !== "object") return false;
  const receipt = value as Record<string, unknown>;
  const runtimeStates = new Set([
    "detached",
    "activating",
    "idle",
    "running",
    "suspended",
  ]);
  return (
    receipt.conversation_id === conversationId &&
    ["project_id", "project_agent_id", "agent_key"].every(
      (key) => typeof receipt[key] === "string" && receipt[key].length > 0,
    ) &&
    (receipt.space_id === undefined ||
      (typeof receipt.space_id === "string" && receipt.space_id.length > 0)) &&
    Number.isInteger(receipt.selection_generation) &&
    (receipt.selection_generation as number) >= 1 &&
    Number.isInteger(receipt.runtime_generation) &&
    (receipt.runtime_generation as number) >= 0 &&
    (receipt.runtime_session_id === null ||
      (typeof receipt.runtime_session_id === "string" &&
        receipt.runtime_session_id.length > 0)) &&
    typeof receipt.runtime_state === "string" &&
    runtimeStates.has(receipt.runtime_state) &&
    (receipt.active_execution_id === null ||
      (typeof receipt.active_execution_id === "string" &&
        receipt.active_execution_id.length > 0))
  );
}

export async function archiveConversation(
  conversationId: string,
): Promise<DurableConversation> {
  const response = await apiRequest<ConversationMutationResponse>(
    `/api/v1/conversations/${conversationId}/archive/`,
    { method: "POST", silent: true },
  );
  return normalizeConversation(response.data);
}

export async function unarchiveConversation(
  conversationId: string,
): Promise<DurableConversation> {
  const response = await apiRequest<ConversationMutationResponse>(
    `/api/v1/conversations/${conversationId}/unarchive/`,
    { method: "POST", silent: true },
  );
  return normalizeConversation(response.data);
}
